import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';

type SelectOptionCardProps = {
  selected: boolean;
  onSelect: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  iconRingClassName?: string;
  className?: string;
  rightSlot?: React.ReactNode;
  role?: 'radio' | 'checkbox' | 'button';
  /** Render as a non-interactive, read-only card (e.g. to show a past selection). */
  disabled?: boolean;
  'aria-label'?: string;
};

export function SelectOptionCard({
  selected,
  onSelect,
  title,
  subtitle,
  icon,
  iconRingClassName,
  className,
  rightSlot,
  role = 'button',
  disabled = false,
  'aria-label': ariaLabel,
}: SelectOptionCardProps): React.ReactElement {
  return (
    <button
      type="button"
      role={role}
      aria-checked={role === 'button' ? undefined : selected}
      aria-label={ariaLabel}
      onClick={disabled ? undefined : onSelect}
      disabled={disabled}
      className={cn(
        'flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors',
        !disabled &&
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        selected
          ? 'border-primary bg-primary/5 shadow-sm'
          : disabled
          ? 'border-border opacity-60'
          : 'border-border hover:bg-muted/40 cursor-pointer',
        disabled && 'cursor-default',
        className
      )}
    >
      {icon !== undefined && (
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            iconRingClassName
          )}
        >
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="text-sm font-semibold text-foreground">{title}</div>
        {subtitle !== undefined && subtitle !== null && subtitle !== '' && (
          <div className="mt-0.5 text-xs leading-snug text-muted-foreground">
            {subtitle}
          </div>
        )}
      </div>
      {rightSlot !== undefined ? (
        rightSlot
      ) : (
        <div
          className={cn(
            'mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
            selected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-muted-foreground/35 bg-background'
          )}
          aria-hidden
        >
          {selected ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
        </div>
      )}
    </button>
  );
}
